import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
} from '@apollo/client';
import {
  UserAcceptTosMutationResult,
  Exact,
  InputMaybe,
} from '../../../../schema/generated/schema';
import FederatedUserService from '../../../../services/federated_identities';
import usersApi from '../../../../services/users';
import {
  WORK_ORIGIN,
  WORKCENTER_ORIGIN,
  API_ORIGIN,
  GET_ORIGIN,
} from '../../../../config';
export interface UpdateFederatedUserPayload {
  email: string;
  xometryUserId: string;
  firstName: string;
  lastName: string;
  intention: string;
  role: string;
  businessSite: string;
  xometryMarketing: boolean;
  thomasMarketing: boolean;
  completingRegistration: boolean;
  thomasOrigin: boolean;
  registrationIntention?: string;
  discovery?: string;
  discoveryOther?: string;

  // These values are associated with xometry ambassador program
  mbsy?: string;
  segmentid?: string;
  campaignid?: string;
}
interface UpdateXometryUserPayload {
  first_name: string;
  last_name: string;
  registration_intention: string;
  company_name?: string;
  mbsy: string;
  segmentid: string;
  campaignid: string;
}
export const completeFederatedUserRegistration = async (
  federatedUserPayload: UpdateFederatedUserPayload
): Promise<boolean> => {
  return FederatedUserService.patchFederatedUser(federatedUserPayload)
    .then((res: { ok: any }) => res.ok)
    .catch(() => false);
};

export const completeXometryUserRegistration = async (
  userId: string,
  firstName: string,
  lastName: string,
  companyName: string | null,
  intentionValue: string,
  userAcceptTos: {
    (
      options?:
        | MutationFunctionOptions<
            UserAcceptTosMutationResult,
            Exact<{ id: string; vendor: InputMaybe<boolean> }>,
            DefaultContext,
            ApolloCache<any>
          >
        | undefined
    ): Promise<
      FetchResult<
        UserAcceptTosMutationResult,
        Record<string, any>,
        Record<string, any>
      >
    >;
    (arg0: { variables: { id: string; vendor: any } }): any;
  },
  setUserApiError: any,
  mbsy: string,
  segmentid: string,
  campaignid: string
) => {
  const payload: UpdateXometryUserPayload = {
    first_name: firstName,
    last_name: lastName,
    registration_intention: intentionValue,
    mbsy,
    segmentid,
    campaignid,
  };
  if (companyName) {
    payload.company_name = companyName;
  }

  const updateUserInfoResponse = await usersApi
    .postUserData(userId, payload)
    .then((res: { ok: any; json: () => any }) => {
      if (res.ok) {
        setUserApiError('');
        return res.json();
      }
      throw new Error('statusError');
    })
    .catch((err: { message: string }) => {
      let errMess = 'There was an error creating your account.';
      if (err.message === 'statusError') {
        errMess = `${errMess} Please try again in a few minutes.`;
      } else {
        errMess = `${errMess} Please try refreshing the page.`;
      }
      setUserApiError(errMess);
    });

  await userAcceptTos({
    variables: {
      id: userId || '',
      vendor: !!updateUserInfoResponse?.company,
    },
  });

  return updateUserInfoResponse || null;
};

const getDomain = (url: URL) => {
  if (url.origin.includes(WORKCENTER_ORIGIN)) {
    return 'workcenter';
  }
  if (url.origin.includes(WORK_ORIGIN)) {
    return 'work';
  }
  if (url.origin.includes(API_ORIGIN)) {
    return 'api';
  }
  if (url.origin.includes(GET_ORIGIN)) {
    return 'get';
  }
  // Default WWW origin as this is the current behavior of supplier registration page
  return 'www';
};

export const redirectUserOnSuccessfulRegistration = (
  intention: string,
  customerRedirectUrl: string,
  fromThomas = false
) => {
  const url = new URL(customerRedirectUrl);
  const domain = getDomain(url);
  if (intention === 'isRoleSupplier' && !fromThomas) {
    window.location.assign(
      `/account/register-supplier?domain=${domain}&redirect=${url.pathname}`
    );
  } else {
    window.location.assign(customerRedirectUrl);
  }
};
